import { makeDraggable } from '../draggable.js';
import { makeResizable } from '../resizable.js';

/**
 * Base class for all draggable/resizable windows in the game.
 * Provides unified state management for position, size, and visibility.
 */
export class BaseWindow {
  // Track all window instances for z-order management
  static instances = new Set();
  // Static z-index counter to track window stacking order
  static nextZIndex = 1000;
  static highestZIndex = 1000;
  static initializing = true; // Track if we're in initialization phase
  constructor(config) {
    // Required configuration
    this.windowId = config.id;
    this.windowTitle = config.title;
    this.windowName = config.name || config.title; // For WindowManager
    
    // Add this instance to the set
    BaseWindow.instances.add(this);
    
    // Optional configuration with defaults
    this.defaultWidth = config.width || 600;
    this.defaultHeight = config.height || 400;
    this.minWidth = config.minWidth || 300;
    this.minHeight = config.minHeight || 200;
    this.maxWidthPercent = config.maxWidthPercent || 80;
    this.maxHeightPercent = config.maxHeightPercent || 80;
    this.titleBarClass = config.titleBarClass || `${this.windowId}-title`;
    
    // State
    this.container = null;
    this.isVisible = this.loadState('visible', false);
    this.zIndex = this.loadState('zIndex', null); // Load saved z-index
    this.cleanup = [];
    
    // Optional callbacks
    this.onInit = config.onInit || null;
    this.onShow = config.onShow || null;
    this.onHide = config.onHide || null;
  }
  
  /**
   * Get storage key for a specific state item
   */
  getStorageKey(item) {
    return `soulforge_${this.windowId}_${item}`;
  }
  
  /**
   * Load state from localStorage
   */
  loadState(item, defaultValue) {
    const key = this.getStorageKey(item);
    const saved = localStorage.getItem(key);
    if (saved === null) return defaultValue;
    
    // Handle different types
    if (item === 'visible') return saved === 'true';
    if (item === 'position' || item === 'size') return JSON.parse(saved);
    if (item === 'zIndex') return parseInt(saved, 10);
    return saved;
  }
  
  /**
   * Save state to localStorage
   */
  saveState(item, value) {
    const key = this.getStorageKey(item);
    if (typeof value === 'object') {
      localStorage.setItem(key, JSON.stringify(value));
    } else {
      localStorage.setItem(key, String(value));
    }
  }
  
  /**
   * Restore z-order for all windows based on saved values
   * Should be called after all windows are initialized
   */
  static restoreZOrder() {
    // Get all windows with their saved z-index values
    const windowsWithZ = Array.from(BaseWindow.instances)
      .filter(window => window.zIndex !== null)
      .sort((a, b) => a.zIndex - b.zIndex);
    
    // Apply z-indices in order to maintain relative positioning
    windowsWithZ.forEach((window, index) => {
      if (window.container) {
        // Use the saved z-index to maintain relative order
        window.container.style.zIndex = window.zIndex;
        
        // Update our tracking
        if (window.zIndex > BaseWindow.highestZIndex) {
          BaseWindow.highestZIndex = window.zIndex;
        }
      }
    });
    
    // Ensure nextZIndex is higher than any restored window
    BaseWindow.nextZIndex = BaseWindow.highestZIndex + 1;
  }
  
  /**
   * Initialize the window
   */
  init() {
    if (this.container) return;
    
    // Create window container
    this.container = document.createElement('div');
    this.container.id = this.windowId;
    
    // Load saved size
    const savedSize = this.loadState('size', {
      width: this.defaultWidth,
      height: this.defaultHeight
    });
    
    // Load saved position (check both new storage key and old draggable.js key)
    let savedPosition = this.loadState('position', null);
    if (!savedPosition) {
      // Check old draggable.js format
      const oldKey = `soulforge_${this.windowId}_position`;
      const oldPos = localStorage.getItem(oldKey);
      if (oldPos) {
        savedPosition = JSON.parse(oldPos);
      }
    }
    
    // Check if saved position is suspicious (0,0 is often a bad default)
    if (savedPosition && savedPosition.left === 0 && savedPosition.top === 0) {
      console.log(`Window ${this.windowId} has suspicious 0,0 position - will center instead`);
      savedPosition = null; // Treat 0,0 as no saved position
    }
    
    // Calculate initial position
    let leftStyle, topStyle, transformStyle = '';
    if (savedPosition) {
      // Check if it's the old format (x, y) or new format (left, top)
      if ('left' in savedPosition && 'top' in savedPosition) {
        // New format - use directly
        leftStyle = `${savedPosition.left}px`;
        topStyle = `${savedPosition.top}px`;
      } else if ('x' in savedPosition && 'y' in savedPosition) {
        // Old format - convert from transform to absolute position
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        leftStyle = `${centerX + savedPosition.x}px`;
        topStyle = `${centerY + savedPosition.y}px`;
      }
    } else {
      // Center the window with safe area consideration
      const viewportWidth = window.visualViewport ? window.visualViewport.width : window.innerWidth;
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      
      // Account for iOS safe areas - ensure windows appear below status bar
      // Status bar is typically 20-44px on iOS devices
      const safeTopMargin = 50; // Safe margin to avoid status bar
      const centerX = viewportWidth / 2;
      const centerY = viewportHeight / 2;
      
      // Calculate position to center the window
      const left = centerX - savedSize.width / 2;
      // Handle 'auto' height - use a reasonable default for centering
      const heightForCentering = savedSize.height === 'auto' ? 400 : savedSize.height;
      const top = Math.max(safeTopMargin, centerY - heightForCentering / 2);
      
      leftStyle = `${left}px`;
      topStyle = `${top}px`;
    }
    
    // Set container styles - handle 'auto' height
    const heightStyle = this.defaultHeight === 'auto' ? 'auto' : `${savedSize.height}px`;
    this.container.style.cssText = `
      position: fixed;
      left: ${leftStyle};
      top: ${topStyle};
      width: ${savedSize.width}px;
      height: ${heightStyle};
      background: rgba(0, 0, 0, 0.95);
      border: 1px solid #0f0;
      box-shadow: 0 0 20px rgba(0, 255, 0, 0.3);
      display: ${this.isVisible ? 'flex' : 'none'};
      flex-direction: column;
      font-family: 'Berkeley Mono', 'Courier New', monospace;
      font-size: 10px;
      color: #0f0;
      transform: ${transformStyle};
      overflow: hidden;
      z-index: ${this.zIndex || BaseWindow.nextZIndex++};
    `;
    
    // Update highest z-index if needed
    const currentZ = parseInt(this.container.style.zIndex, 10);
    if (currentZ > BaseWindow.highestZIndex) {
      BaseWindow.highestZIndex = currentZ;
    }
    
    // During initialization, also update nextZIndex to avoid conflicts
    if (BaseWindow.initializing && currentZ >= BaseWindow.nextZIndex) {
      BaseWindow.nextZIndex = currentZ + 1;
    }
    
    // Save z-index if we just assigned a new one
    if (!this.zIndex) {
      this.zIndex = currentZ;
      this.saveState('zIndex', this.zIndex);
    }
    
    // After a short delay, mark initialization as complete
    setTimeout(() => {
      BaseWindow.initializing = false;
    }, 100);
    
    // Create title bar
    const titleBar = this.createTitleBar();
    this.container.appendChild(titleBar);
    
    // Create content area
    const content = this.createContent();
    if (content) {
      this.container.appendChild(content);
    }
    
    // Add to DOM
    document.body.appendChild(this.container);
    
    // Make draggable and resizable
    makeDraggable(this.container, { 
      name: this.windowName,
      onDragEnd: () => {
        // Save position immediately while window is still visible
        if (this.isVisible) {
          this.saveCurrentPosition();
        }
      }
    });
    
    // Bring to front on click (including when starting to drag)
    this.container.addEventListener('mousedown', () => {
      this.bringToFront();
    });
    
    // Only make resizable if not using auto height
    if (this.defaultHeight !== 'auto') {
      makeResizable(this.container, {
        minWidth: this.minWidth,
        minHeight: this.minHeight,
        maxWidthPercent: this.maxWidthPercent,
        maxHeightPercent: this.maxHeightPercent,
        onResize: (width, height) => this.saveCurrentSize(width, height)
      });
    }
    
    // Call init callback if provided
    if (this.onInit) {
      this.onInit(this.container);
    }
  }
  
  /**
   * Create the title bar
   */
  createTitleBar() {
    const titleBar = document.createElement('div');
    titleBar.className = this.titleBarClass;
    titleBar.style.cssText = `
      background: rgba(0, 255, 0, 0.1);
      border-bottom: 1px solid #0f0;
      padding: 8px 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      user-select: none;
    `;
    
    titleBar.innerHTML = `
      <span class="window-title">${this.windowTitle}</span>
      <button class="window-close-btn" style="
        background: transparent;
        border: 1px solid #0f0;
        color: #0f0;
        padding: 2px 8px;
        cursor: pointer;
        font-size: 14px;
        line-height: 1;
      ">×</button>
    `;
    
    // Handle close button
    const closeBtn = titleBar.querySelector('.window-close-btn');
    closeBtn.addEventListener('click', () => this.hide());
    
    return titleBar;
  }
  
  /**
   * Create the content area (override in subclasses)
   */
  createContent() {
    // Override in subclasses
    return null;
  }
  
  /**
   * Save current position
   */
  saveCurrentPosition() {
    if (!this.container) return;
    
    // If window is not visible, we can't get accurate position
    if (!this.isVisible || this.container.style.display === 'none') {
      // Use cached position if available
      if (this.lastKnownPosition) {
        this.saveState('position', this.lastKnownPosition);
      }
      return;
    }
    
    const rect = this.container.getBoundingClientRect();
    
    // Don't save positions that are likely errors
    // (0,0 with positive width/height means window is visible but position is wrong)
    if (rect.left === 0 && rect.top === 0 && rect.width > 0 && rect.height > 0) {
      console.log(`Window ${this.windowId} would save suspicious 0,0 position - skipping save`);
      return; // Skip saving suspicious 0,0 position
    }
    
    // Don't save if window has zero dimensions (shouldn't happen if visible)
    if (rect.width === 0 || rect.height === 0) {
      console.log(`Window ${this.windowId} has zero size - skipping position save`);
      return;
    }
    
    const computedStyle = window.getComputedStyle(this.container);
    const transform = computedStyle.transform;
    
    let transformX = 0, transformY = 0;
    if (transform && transform !== 'none') {
      const matrix = new DOMMatrix(transform);
      transformX = matrix.m41;
      transformY = matrix.m42;
    }
    
    // Save absolute position
    const position = {
      left: rect.left,
      top: rect.top
    };
    
    // Cache this position for when window is hidden
    this.lastKnownPosition = position;
    
    this.saveState('position', position);
  }
  
  /**
   * Save current size
   */
  saveCurrentSize(width, height) {
    if (!this.container) return;
    
    // Use provided dimensions or get from element
    if (width === undefined || height === undefined) {
      const rect = this.container.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
    }
    
    this.saveState('size', {
      width: width,
      height: height
    });
  }
  
  /**
   * Show the window
   */
  show() {
    if (!this.container) {
      this.init();
    }
    
    // Check if we have a saved position - if not or if it's bad, center the window
    const savedPosition = this.loadState('position', null);
    
    // Also check the actual current position of the window
    const currentRect = this.container.getBoundingClientRect();
    const currentlyAt00 = currentRect.left === 0 && currentRect.top === 0 && 
                         currentRect.width > 0 && currentRect.height > 0;
    
    // Check if position is missing or suspicious (0,0 is often a bad default)
    const needsCentering = !savedPosition || 
                          (savedPosition && savedPosition.left === 0 && savedPosition.top === 0) ||
                          (savedPosition && (savedPosition.left < 0 || savedPosition.top < 0)) ||
                          currentlyAt00; // Also center if window is currently at 0,0
    
    if (needsCentering) {
      // Get saved size for centering calculation
      const savedSize = this.loadState('size', {
        width: this.defaultWidth,
        height: this.defaultHeight
      });
      
      // Center the window
      const viewportWidth = window.visualViewport ? window.visualViewport.width : window.innerWidth;
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      
      const safeTopMargin = 50; // Safe margin to avoid status bar
      const centerX = viewportWidth / 2;
      const centerY = viewportHeight / 2;
      
      // Calculate position to center the window
      const left = centerX - savedSize.width / 2;
      const heightForCentering = savedSize.height === 'auto' ? 400 : savedSize.height;
      const top = Math.max(safeTopMargin, centerY - heightForCentering / 2);
      
      this.container.style.left = `${left}px`;
      this.container.style.top = `${top}px`;
    }
    
    this.container.style.display = 'flex';
    this.isVisible = true;
    this.saveState('visible', true);
    
    // Bring to front when showing
    this.bringToFront();
    
    // Call show callback if provided
    if (this.onShow) {
      this.onShow();
    }
  }
  
  /**
   * Hide the window
   */
  hide() {
    if (!this.container) return;
    
    // Save current position and size WHILE window is still visible
    if (this.isVisible && this.container.style.display !== 'none') {
      const rect = this.container.getBoundingClientRect();
      
      // Only save if we have valid dimensions
      if (rect.width > 0 && rect.height > 0) {
        // Don't save 0,0 positions
        if (!(rect.left === 0 && rect.top === 0)) {
          this.saveState('position', {
            left: rect.left,
            top: rect.top
          });
          this.lastKnownPosition = {
            left: rect.left,
            top: rect.top
          };
        }
        
        // Save size
        this.saveState('size', {
          width: rect.width,
          height: rect.height
        });
      }
    }
    
    // Now hide the window
    this.container.style.display = 'none';
    this.isVisible = false;
    this.saveState('visible', false);
    
    // Call hide callback if provided
    if (this.onHide) {
      this.onHide();
    }
  }
  
  /**
   * Toggle window visibility
   */
  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }
  
  /**
   * Ensure window is fully visible on screen
   * Adjusts position if window extends beyond viewport
   */
  ensureWindowOnScreen() {
    if (!this.container || !this.isVisible) return;
    
    // Small delay to let content changes settle
    setTimeout(() => {
      const rect = this.container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let needsUpdate = false;
      let newLeft = rect.left;
      let newTop = rect.top;
      
      // Check right edge
      if (rect.right > viewportWidth) {
        newLeft = viewportWidth - rect.width;
        needsUpdate = true;
      }
      
      // Check left edge
      if (rect.left < 0) {
        newLeft = 0;
        needsUpdate = true;
      }
      
      // Check bottom edge
      if (rect.bottom > viewportHeight) {
        newTop = viewportHeight - rect.height;
        needsUpdate = true;
      }
      
      // Check top edge
      if (rect.top < 0) {
        newTop = 0;
        needsUpdate = true;
      }
      
      // Apply new position if needed
      if (needsUpdate) {
        this.container.style.left = `${newLeft}px`;
        this.container.style.top = `${newTop}px`;
        this.saveCurrentPosition();
      }
    }, 10);
  }
  
  /**
   * Bring window to front by updating z-index
   */
  bringToFront() {
    if (!this.container) return;
    
    // Increment highest z-index and apply
    BaseWindow.highestZIndex++;
    this.zIndex = BaseWindow.highestZIndex;
    this.container.style.zIndex = this.zIndex;
    
    // Save new z-index
    this.saveState('zIndex', this.zIndex);
  }
  
  /**
   * Destroy the window and clean up
   */
  destroy() {
    // Remove from instances set
    BaseWindow.instances.delete(this);
    
    // Run cleanup functions
    this.cleanup.forEach(fn => fn());
    
    // Remove from DOM
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}